<template>
    <div class="flex flex-column align-items-center w-full mb-8">
        <div class="px-3 lg:px-4 py-5 w-full bg-white shadow-1 border-round">
            <div class="flex flex-column lg:flex-row w-full">

                <!-- Features List - For Desktop -->
                <div class="hidden lg:flex flex-column justify-content-end flex-1 pkg-col">
                    <div v-for="(def, index) in featureDefinitions" :key="def.key"
                        :class="['feature-row justify-content-start border-bottom-1 border-gray-200 font-normal text-500 pl-2 text-sm', { 'border-top-1': index === 0 }]">
                        {{ def.label }}
                        <i v-if="def.info" class="pi pi-info-circle text-xs ml-2 cursor-pointer" :title="def.info"></i>
                    </div>
                </div>

                <div v-for="(pkg, index) in packages" :key="pkg.id + index"
                    class="flex flex-column align-items-center text-center border-left-none lg:border-left-1 border-gray-200 flex-1 pkg-col mb-6 lg:mb-0 relative">

                    <div v-if="pkg.popular" class="absolute" style="top: -15px;">
                        <span class="bg-green-100 text-green-600 border-round-2xl px-3 py-1 text-xs font-semibold border-1 border-green-300">
                            Popular
                        </span>
                    </div>

                    <div class="pkg-header-title w-full mt-4 px-4 lg:px-0 xl:px-4">
                        <h3 class="text-lg lg:text-sm xl:text-base font-medium text-800 m-0">{{ pkg.name }}</h3>
                    </div>

                    <div class="pkg-header-price w-full mt-3 mb-2 px-4 lg:px-0 lg:px-4 flex align-items-baseline justify-content-center">
                        <span class="text-xl font-medium mr-1 text-800">Rs.</span>
                        <span class="text-6xl lg:text-4xl xl:text-5xl 2xl:text-6xl font-bold H6-Medium">{{ pkg.priceLabel }}</span>
                    </div>

                    <p class="text-600 text-xs mb-6">per month/ user</p>

                    <div class="pkg-header-button w-full px-2 lg:px-4 mb-4">
                        <Button 
                            :label="getButtonLabel(pkg.id)"
                            :class="getButtonClass(pkg.id, pkg.popular)"
                            :disabled="isButtonDisabled(pkg.id)"
                            @click="selectPackage(pkg, getButtonAction(pkg.id))" 
                        />
                    </div>

                    <!-- Features - For Desktop -->
                    <div class="w-full hidden lg:block mt-6">
                        <div v-for="(def, index) in featureDefinitions" :key="def.key"
                            :class="['feature-row border-bottom-1 border-gray-200 justify-content-center w-full', { 'border-top-1': index === 0 }]">
                            <span v-if="typeof pkg.features[def.key] === 'string' || typeof pkg.features[def.key] === 'number'" class="text-sm font-normal text-600">
                                {{ pkg.features[def.key] }}
                            </span>
                            <template v-else>
                                <i class="pi pi-check text-600 text-sm" v-if="pkg.features[def.key] === true"></i>
                                <i class="pi pi-times text-600 text-sm" v-else></i>
                            </template>
                        </div>
                    </div>

                    <!-- Features - For Mobile -->
                    <div class="w-full lg:hidden mt-4 border-round">
                        <div v-for="def in featureDefinitions" :key="def.key"
                            class="flex justify-content-between align-items-center py-3 px-3 border-bottom-1 border-gray-200">
                            <span class="text-sm text-500 font-normal text-left">{{ def.label }}</span>
                            <span>
                                <span v-if="typeof pkg.features[def.key] === 'string' || typeof pkg.features[def.key] === 'number'" class="text-sm font-normal text-600">
                                    {{ pkg.features[def.key] }}
                                </span>
                                <template v-else>
                                    <i class="pi pi-check text-600 text-sm" v-if="pkg.features[def.key] === true"></i>
                                    <i class="pi pi-times text-600 text-sm" v-else></i>
                                </template>
                            </span>
                        </div>
                    </div>
                </div>

            </div>
        </div>

        <!-- <div class="w-full bg-white shadow-1 border-round p-5 flex flex-column lg:flex-row justify-content-between align-items-center">
            <div class="flex-1 mb-4 lg:mb-0">
                <h3 class="text-xl font-bold text-800 mb-2 m-0">Want customization</h3>
                <p class="text-600 text-sm m-0 mb-4 line-height-3">Built around your learning goals. Customize features, <br> access, and support to match your needs.</p>
                <Button label="Talk with us" class="custom-brown-btn px-5 font-bold" @click="$emit('contact-sales')" />
            </div>

            <div class="flex-1 flex flex-column justify-content-center pl-0 lg:pl-5">
                <ul class="list-none p-0 m-0 text-600 text-sm">
                    <li class="mb-3 flex align-items-center"><i class="pi pi-check text-500 mr-2 text-xs"></i> Custom feature bundle</li>
                    <li class="mb-3 flex align-items-center"><i class="pi pi-check text-500 mr-2 text-xs"></i> Flexible billing options</li>
                    <li class="mb-3 flex align-items-center"><i class="pi pi-check text-500 mr-2 text-xs"></i> Dedicated support </li>
                    <li class="flex align-items-center"><i class="pi pi-check text-500 mr-2 text-xs"></i> Volume pricing for teams</li>
                </ul>
            </div>
        </div> -->

        <!-- Disclaimer -->
        <div class="text-center w-full max-w-4xl pt-6 pb-2">
            <h3 class="disclaimer-heading text-lg font-semibold mb-2" style="color: #965903;">Disclaimer</h3>
            <p class="text-700 text-sm m-0 mb-2">
                All payments must be processed through the platform. ILearn'd Tutors is not responsible for issues caused by third-party payment providers or user error.
            </p>
            <p class="text-700 text-sm m-0">
                Feature availability may vary depending on your subscription plan and development stage.
            </p>
        </div>

    </div>
</template>

<script setup>
import { defineProps, defineEmits } from 'vue';
import Button from 'primevue/button';

const props = defineProps({
    // 'onboarding' or 'settings'
    mode: {
        type: String,
        default: 'onboarding'
    },
    // The ID of the currently active plan (only matters if mode is 'settings')
    currentPlanId: {
        type: String,
        default: null
    },
    // Optional: Pass these as props if the text changes between views
    featureDefinitions: {
        type: Array,
        required: true
    },
    packages: {
        type: Array,
        required: true
    },
    disableActions: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['package-action', 'contact-sales']);

const getButtonAction = (pkgId) => {
    if (props.mode === 'onboarding') return 'start';
    if (pkgId === props.currentPlanId) return 'current';
    const currentIdx = props.packages.findIndex(p => p.id === props.currentPlanId);
    const targetIdx  = props.packages.findIndex(p => p.id === pkgId);
    return targetIdx > currentIdx ? 'upgrade' : 'downgrade';
};

const getButtonLabel = (pkgId) => {
    const pkg = props.packages.find(p => p.id === pkgId);
    if (pkg?.actionLabel) return pkg.actionLabel;

    const action = getButtonAction(pkgId);
    switch (action) {
        case 'start': return 'Get Started';
        case 'current': return 'Your current plan';
        case 'upgrade': return 'Upgrade';
        case 'downgrade': return 'Downgrade';
        default: return 'Select';
    }
};

const getButtonClass = (pkgId, isPopular) => {
    const action = getButtonAction(pkgId);
    
    if (action === 'current') return 'p-button-outlined p-button-secondary w-full opacity-60';
    
    // Primary styling for popular plans
    if (isPopular) {
        return 'custom-brown-btn w-full';
    }
    
    if (action === 'downgrade') return 'p-button-outlined p-button-secondary w-full text-700';

    // Outlined for non-popular upgrades/starts
    if (action === 'upgrade' || action === 'start') {
        return 'p-button-outlined p-button-secondary w-full text-700 font-bold';
    }
    
    return 'p-button-outlined p-button-secondary w-full text-700';
};

const isButtonDisabled = (pkgId) => {
    if (props.disableActions) return true;

    const pkg = props.packages.find(p => p.id === pkgId);
    if (pkg && typeof pkg.disabled === 'boolean') {
        return pkg.disabled;
    }

    return getButtonAction(pkgId) === 'current';
};

const selectPackage = (pkg, action) => {
    emit('package-action', { package: pkg, action: action });
};
</script>

<style scoped>
.pkg-col {
    flex: 1 1 0%;
    min-width: 0;
}

.feature-row {
    min-height: 55px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px 4px;
    word-break: break-word;
}

.text-500 {
    color: #7c858d !important;
}

.pkg-header-title {
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.pkg-header-price {
    height: 60px;
}

.pkg-header-button {
    height: 45px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.pkg-header-button :deep(.p-button) {
    font-size: 1rem;
}

@media (min-width: 990px) {
    .pkg-header-button :deep(.p-button) {
        font-size: 0.8rem;
    }
}

@media (min-width: 1200px) {
    .pkg-header-button :deep(.p-button) {
        font-size: 1rem;
    }
}

.custom-brown-btn {
    background-color: var(--yellow-500) !important;
    border: 1px solid var(--yellow-500) !important;
    color: white !important;
    font-weight: 600;
}

.custom-brown-btn:hover {
    background-color: var(--yellow-600) !important;
    border-color: var(--yellow-600) !important;
}

.p-button-outlined {
    background-color: var(--accent-50) !important;
    color: black !important;
    font-weight: 600;
}

.p-button-outlined:not(:disabled):hover {
    background-color: var(--accent-100) !important;
}
</style>